import React, { useContext, useEffect, useMemo } from 'react';
import { useForm, FormProvider, UseFormSetError, FieldValues } from 'react-hook-form';
import classNames from 'classnames';

import useApi, { UseInvalidateEndpoint } from 'common/useApi';
import useFormApiErrors from 'common/useFormApiErrors';

import FormLabel from 'components/FormLabel';
import Loading from 'components/Loading';
import AuthContext from 'common/auth';

// The browser knows the full IANA list; fall back to a small set on the rare
// engine without Intl.supportedValuesOf so the picker is never empty.
const FALLBACK_ZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Shanghai',
  'Asia/Tokyo',
];

const getTimezones = (): string[] => {
  const supportedValuesOf = (Intl as unknown as {
    supportedValuesOf?: (key: string) => string[]
  }).supportedValuesOf;

  try {
    return supportedValuesOf ? supportedValuesOf('timeZone') : FALLBACK_ZONES;
  } catch {
    return FALLBACK_ZONES;
  }
};

const TimezoneSettings = () => {
  const { user, refetch } = useContext(AuthContext)!;
  const { isLoading, isFetching, isError, isSuccess, error, mutate } = useApi(
    'post', '/api/me/update-timezone',
  );
  const invalidate = UseInvalidateEndpoint();

  const timezones = useMemo(getTimezones, []);

  const formMethods = useForm({
    defaultValues: { timezone: user?.timezone ?? 'America/New_York' },
    mode: 'onChange',
  });
  const { handleSubmit, register, reset, setError, formState: { isSubmitting, isDirty } } = formMethods;

  // Keep the selected value in sync once the user loads / changes elsewhere.
  useEffect(() => {
    if (user?.timezone) {
      reset({ timezone: user.timezone });
    }
  }, [user?.timezone]);

  useFormApiErrors(
    isError,
    error,
    setError as unknown as UseFormSetError<FieldValues>,
    isSuccess,
    'Timezone updated — graphs now show your local time',
  );

  useEffect(() => {
    if (isSuccess) {
      invalidate('/api/me'); // timezone changed
      refetch(); // refresh AuthContext so the dashboard re-buckets immediately
    }
  }, [isSuccess]);

  const onSubmit = ({ timezone }: { timezone: string }) => {
    mutate({ timezone });
  };

  const formDisabled = isSubmitting || isLoading || isFetching || !isDirty;

  return (
    <>
      <h3>Timezone</h3>
      <div className={classNames({ box: true, isLoading })}>
        {isLoading && <Loading />}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormLabel
              field='timezone'
              name='Times on your dashboard graphs are shown in this timezone'
              className='half'
            >
              <select {...register('timezone', { required: true })}>
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </FormLabel>
            <button type='submit' className='primary big' disabled={formDisabled}>
              Save Timezone
            </button>
          </form>
        </FormProvider>
      </div>
    </>
  );
};

export default TimezoneSettings;
